import * as React from 'react';
import APICall, { HttpResponse } from 'store/APIClient';
import _ from 'lodash';
import LanguageIcon from '@mui/icons-material/Language';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import {
    DASHBOARD_SCOPE,
    DashboardScopeT,
    DashboardT,
} from 'business/definitions';
import { ScheduledDashboardEmail } from 'components/DashboardSendModal/ScheduledDashboardEmailConfigForm/types';
import { shouldSendEmailRightAway } from 'components/DashboardSendModal/helpers';

export function getDashboardScope(dashboard: DashboardT): DashboardScopeT {
    if (dashboard.isTemplate) return DASHBOARD_SCOPE.TEMPLATE;
    if (dashboard.isTutorial) return DASHBOARD_SCOPE.TUTORIAL;
    if (dashboard.orgSharing) {
        if (dashboard.orgSharing.isPublic) return DASHBOARD_SCOPE.PUBLIC;
        if (dashboard.orgSharing.isRestricted)
            return DASHBOARD_SCOPE.RESTRICTED;
        if (
            dashboard.usersSharing.length > 0 ||
            dashboard.groupsSharing.length > 0
        )
            return DASHBOARD_SCOPE.SHARED;
    }
    return DASHBOARD_SCOPE.PRIVATE;
}
/**
 * Sort dashboards according to their scope. We display a dashboard at the higher scope it is shared.
 * For instance dashboard public (read only) with edit access at restricted scope will be
 * displayed in the 'public' column.
 */
export function splitDashboardsByScope(
    dashboards: DashboardT[],
): Record<DashboardScopeT, DashboardT[]> {
    // @ts-expect-error [TS migration] Was not detected by flow
    return _.groupBy(dashboards, getDashboardScope);
}

/**
 * Centralized scope icon objects.
 * @param {string} scope value of the scope
 */
export function getIconFromScope(scope: DashboardScopeT): JSX.Element {
    switch (scope) {
        case DASHBOARD_SCOPE.PRIVATE:
            return <LockIcon />;
        case DASHBOARD_SCOPE.SHARED:
            return <PeopleIcon />;
        case DASHBOARD_SCOPE.RESTRICTED:
            return <AccountBalanceIcon />;
        case DASHBOARD_SCOPE.PUBLIC:
            return <LanguageIcon />;
        case DASHBOARD_SCOPE.TEMPLATE:
            return <ScreenShareIcon />;
        default:
            return <></>;
    }
}

export function sendScheduledDashboardEmailNow(
    dashboardKey: number,
    scheduledEmail: ScheduledDashboardEmail,
) {
    // we don't want to wait for this mail to be sent before sending the config, so we let it
    // run asynchronously
    APICall({
        endpoint: `explore/dashboard_emails/${dashboardKey}`,
        method: 'POST',
        body: scheduledEmail,
    });
}

export function sendScheduledDashboardEmail(
    dashboardKey: number,
    scheduledEmail: ScheduledDashboardEmail,
): Promise<HttpResponse<object>> {
    if (shouldSendEmailRightAway(scheduledEmail.config)) {
        sendScheduledDashboardEmailNow(dashboardKey, scheduledEmail);
    }

    // we save the scheduled every time
    return APICall({
        endpoint: `explore/scheduled_dashboard_emails/${dashboardKey}`,
        method: 'POST',
        body: scheduledEmail,
    });
}

/**
 * Check if we are currently on a public dashboard page, based on pathname.
 * @returns boolean
 */
export const isCurrentlyOnPublicDashboard = (): boolean =>
    window.location.pathname.includes('/public/dashboard/');
